import type { EChartsOption } from 'echarts';
import type { ChartData, TopItemsData } from '@/entities/dashboard';

export const createAreaChartOption = (data: ChartData): EChartsOption => ({
  title: {
    text: data.title,
    left: 'center',
    textStyle: {
      fontSize: 16,
      fontWeight: 'normal'
    }
  },
  tooltip: {
    trigger: 'axis',
    axisPointer: {
      type: 'cross',
      label: {
        backgroundColor: '#6a7985'
      }
    },
    formatter: (params) => {
      if (Array.isArray(params)) {
        let result = `<div><strong>${params[0].name}</strong></div>`;
        params.forEach((param) => {
          const value = Number(param.value) >= 1000 ? `${(Number(param.value) / 1000).toFixed(1)}k` : param.value;
          result += `<div>${param.marker} ${param.seriesName}: <strong>${value}${data.unitLabel || ''}</strong></div>`;
        });
        return result;
      }
      return '';
    }
  },
  legend: {
    data: data.series.map((s) => s.name),
    bottom: 0
  },
  grid: {
    left: '3%',
    right: '4%',
    bottom: '18%',
    top: '15%',
    containLabel: true
  },
  xAxis: {
    type: 'category',
    boundaryGap: false,
    data: data.categories
  },
  yAxis: {
    type: 'value',
    axisLabel: {
      formatter: (value: number) => {
        if (value >= 1000) {
          return (value / 1000).toFixed(1) + 'k';
        }
        return value.toString();
      }
    }
  },
  dataZoom: [
    {
      type: 'inside',
      start: 0,
      end: 100,
      xAxisIndex: 0
    },
    {
      type: 'slider',
      show: true,
      start: 0,
      end: 100,
      bottom: 40,
      height: 20,
      borderColor: 'transparent',
      backgroundColor: '#f4f4f4',
      fillerColor: 'rgba(144, 147, 153, 0.3)',
      handleStyle: {
        color: '#6190e8',
        borderWidth: 0
      },
      moveHandleStyle: {
        color: '#6190e8'
      }
    }
  ],
  series: data.series.map((series) => ({
    name: series.name,
    type: 'line',
    stack: series.stack || 'Total',
    areaStyle: {
      opacity: 0.6
    },
    emphasis: {
      focus: 'series'
    },
    data: series.data,
    itemStyle: {
      color: series.color
    },
    lineStyle: {
      width: 2
    },
    smooth: true
  }))
});

export const createColumnChartOption = (data: ChartData): EChartsOption => ({
  title: {
    text: data.title,
    left: 'center',
    textStyle: {
      fontSize: 16,
      fontWeight: 'normal'
    }
  },
  tooltip: {
    trigger: 'axis',
    axisPointer: {
      type: 'shadow'
    },
    formatter: (params) => {
      if (Array.isArray(params)) {
        let result = `<div><strong>${params[0].name}</strong></div>`;
        params.forEach((param) => {
          const value = Number(param.value) >= 1000 ? `${(Number(param.value) / 1000).toFixed(1)}k` : param.value;
          result += `<div>${param.marker} ${param.seriesName}: <strong>${value}${data.unitLabel || ''}</strong></div>`;
        });
        return result;
      }
      return '';
    }
  },
  legend: {
    data: data.series.map((s) => s.name),
    bottom: 0
  },
  grid: {
    left: '3%',
    right: '4%',
    bottom: '18%',
    top: '15%',
    containLabel: true
  },
  xAxis: {
    type: 'category',
    data: data.categories
  },
  yAxis: {
    type: 'value',
    axisLabel: {
      formatter: (value: number) => {
        if (value >= 1000) {
          return (value / 1000).toFixed(1) + 'k';
        }
        return value.toString();
      }
    }
  },
  dataZoom: [
    {
      type: 'inside',
      start: 0,
      end: 10,
      xAxisIndex: 0
    },
    {
      type: 'slider',
      show: true,
      start: 0,
      end: 10,
      bottom: 40,
      height: 20,
      borderColor: 'transparent',
      backgroundColor: '#f4f4f4',
      fillerColor: 'rgba(144, 147, 153, 0.3)',
      handleStyle: {
        color: '#6190e8',
        borderWidth: 0
      },
      moveHandleStyle: {
        color: '#6190e8'
      }
    }
  ],
  series: data.series.map((series) => ({
    name: series.name,
    type: 'bar',
    stack: series.stack,
    data: series.data,
    itemStyle: {
      color: series.color
    }
  }))
});

export const createBarChartOption = (data: TopItemsData): EChartsOption => ({
  tooltip: {
    trigger: 'item',
    formatter: '{b}: <strong>{c}%</strong>'
  },
  grid: {
    left: '3%',
    right: '4%',
    bottom: '3%',
    top: '3%',
    containLabel: true
  },
  xAxis: {
    type: 'value',
    max: 100,
    axisLabel: {
      formatter: '{value}%'
    }
  },
  yAxis: {
    type: 'category',
    data: data.series.map((s) => s.name),
    inverse: true,
    axisLabel: {
      interval: 0,
      formatter: (value: string) => {
        return value.length > 20 ? value.substring(0, 20) + '...' : value;
      }
    }
  },
  series: [
    {
      type: 'bar',
      data: data.series.map((s) => ({
        value: s.percentage,
        itemStyle: { color: s.color }
      })),
      barWidth: '60%'
    }
  ]
});

export const createPieChartOption = (data: TopItemsData, title?: string): EChartsOption => ({
  title: title
    ? {
        text: title,
        left: 'center',
        textStyle: {
          fontSize: 16,
          fontWeight: 'normal'
        }
      }
    : undefined,
  tooltip: {
    trigger: 'item',
    formatter: '{b}: <strong>{c}%</strong>'
  },
  legend: {
    orient: 'vertical',
    left: 'left'
  },
  series: [
    {
      type: 'pie',
      radius: '50%',
      center: ['50%', '50%'],
      data: data.series.map((s) => ({
        value: s.percentage,
        name: s.name,
        itemStyle: { color: s.color }
      })),
      emphasis: {
        itemStyle: {
          shadowBlur: 10,
          shadowOffsetX: 0,
          shadowColor: 'rgba(0, 0, 0, 0.5)'
        }
      }
    }
  ]
});
